import { FC, useRef, useMemo, useEffect } from 'react';
import ReactQuill from 'react-quill-new';
import { useTranslation } from 'react-i18next';
import { getMessageApi } from '@/utils/messageClient';
import 'react-quill-new/dist/quill.snow.css';
import styles from './index.module.scss';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  readOnly?: boolean;
  className?: string;
}

// 允许的格式白名单
const formats = ['bold', 'italic', 'underline', 'list', 'align'];

const RichTextEditor: FC<RichTextEditorProps> = ({ value, onChange, readOnly, className }) => {
  const { t } = useTranslation();
  const quillRef = useRef<ReactQuill>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // 为工具栏按钮添加 tooltip
  useEffect(() => {
    if (!containerRef.current) return;

    const tooltipMap: Record<string, string> = {
      '.ql-bold': t('richTextEditor.bold', 'Bold'),
      '.ql-italic': t('richTextEditor.italic', 'Italic'),
      '.ql-underline': t('richTextEditor.underline', 'Underline'),
      '.ql-list[value="ordered"]': t('richTextEditor.orderedList', 'Ordered List'),
      '.ql-list[value="bullet"]': t('richTextEditor.bulletList', 'Bullet List'),
      '.ql-align': t('richTextEditor.align', 'Text Alignment'),
      '.ql-clean': t('richTextEditor.clean', 'Clear Formatting'),
      '.ql-copyText': t('richTextEditor.copy', 'Copy Text'),
      '.ql-clearAll': t('richTextEditor.clearAll', 'Clear All'),
    };

    Object.entries(tooltipMap).forEach(([selector, tooltip]) => {
      const button = containerRef.current?.querySelector(selector);
      if (button) {
        button.setAttribute('title', tooltip);
      }
    });
  }, [t]);

  // 自定义工具栏 handlers
  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          ['bold', 'italic', 'underline'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ align: [] }], // 对齐菜单：左对齐、居中、右对齐、两端对齐
          ['clean'],
          ['copyText', 'clearAll'], // 自定义按钮
        ],
        handlers: {
          // 复制纯文本
          copyText: function () {
            const editor = quillRef.current?.getEditor();
            if (!editor) return;
            const text = editor.getText();
            navigator.clipboard
              .writeText(text.trim())
              .then(() => {
                getMessageApi()?.success(t('coverLetterGeneration.copySuccess'));
              })
              .catch(() => {
                getMessageApi()?.error(t('coverLetterGeneration.copyFailed'));
              });
          },
          // 清除所有格式
          clearAll: function () {
            const editor = quillRef.current?.getEditor();
            if (!editor) return;
            const text = editor.getText();
            editor.setText(text.trim());
            getMessageApi()?.success(t('coverLetterGeneration.formatCleared'));
          },
        },
      },
      clipboard: {
        matchVisual: false,
      },
    }),
    [t]
  );

  return (
    <div ref={containerRef} className={`${styles.richTextEditor} ${className || ''}`}>
      <ReactQuill ref={quillRef} value={value} onChange={onChange} modules={modules} formats={formats} readOnly={readOnly} theme="snow" />
    </div>
  );
};

export default RichTextEditor;
